import { Signal } from 'typed-signals';
import { BlockerType } from '../../types';
import type {
  AdBlockerState,
  BlockerExportState,
  BlockerModel,
  KnotBlockerState,
  PlankBlockerState,
  ScoreBlockerState,
  TapeBlockerState,
  BirdHouseBlockerState,
  CoinBlockerState,
  RocketBlockerState,
  MeasureTapeBlockerState,
} from '../../types';
import type { TileStackModel } from '../board/TileStackModel';
import { ScoreBlockerModel } from './ScoreBlockerModel';
import { AdBlockerModel } from './AdBlockerModel';
import { PlankBlockerModel } from './PlankBlockerModel';
import { removeElement } from '../../../../utils/array-utils';
import { TapeBlockerModel } from './TapeBlockerModel';
import { KnotBlockerModel } from './KnotBlockerModel';
import { BirdHouseBlockerModel } from './BirdHouseBlockerModel';
import { StarBlockerModel } from './StarBlockerModel';
import type { LevelTaskProgress, LevelTaskType } from '../../../tasks/types';
import { CoinBlockerModel } from './CoinBlockerModel';
import { RocketBlockerModel } from './RocketBlockerModel';
import type { BoardModel } from '../board/BoardModel';
import { MeasureTapeBlockerModel } from './MeasureTapeBlockerModel';
import type { TileModel } from '../board/TileModel';
import { generateId } from '../../../../utils/generateId';
import type { TasksRunner } from '../../../../libs/TasksRunner';

export class BlockersFactory {
  public onBlockerChanged = new Signal<(stack: TileStackModel, blocker: BlockerModel) => void>();

  public onRemovalRequest = new Signal<(stack: TileStackModel, blocker: BlockerModel) => void>();

  public onReleaseCollectable = new Signal<(stack: TileStackModel, blocker: BlockerModel) => void>();

  private readonly blockers: BlockerModel[] = [];

  constructor(
    private readonly boardModel: BoardModel,
    private readonly tasksRunner: TasksRunner,
  ) {}

  public reset(): void {
    this.blockers.length = 0;
    this.onBlockerChanged.disconnectAll();
    this.onRemovalRequest.disconnectAll();
    this.onReleaseCollectable.disconnectAll();
  }

  public addBlockerToStack(stack: TileStackModel, blockerState: BlockerExportState): BlockerModel {
    const { type } = blockerState;

    const blocker: BlockerModel = {
      [BlockerType.Score]: () => new ScoreBlockerModel(blockerState as ScoreBlockerState),
      [BlockerType.Ad]: () => new AdBlockerModel(blockerState as AdBlockerState),
      [BlockerType.Plank]: () => new PlankBlockerModel(stack, blockerState as PlankBlockerState),
      [BlockerType.Tape]: () => new TapeBlockerModel(stack, blockerState as TapeBlockerState),
      [BlockerType.Knot]: () => new KnotBlockerModel(stack, blockerState as KnotBlockerState),
      [BlockerType.Star]: () => new StarBlockerModel(stack),
      [BlockerType.BirdHouse]: () =>
        new BirdHouseBlockerModel(
          stack,
          this.boardModel.getScheduler(),
          this.tasksRunner,
          blockerState as BirdHouseBlockerState,
        ),
      [BlockerType.Coin]: () => new CoinBlockerModel(blockerState as CoinBlockerState),
      [BlockerType.Rocket]: () =>
        new RocketBlockerModel(stack, this.boardModel, this.tasksRunner, blockerState as RocketBlockerState),
      [BlockerType.MeasureTape]: () => new MeasureTapeBlockerModel(stack, blockerState as MeasureTapeBlockerState),
    }[type]();

    blocker?.requestRemoval?.connect(() => {
      this.onRemovalRequest.emit(stack, blocker);
      removeElement(this.blockers, blocker);
    });

    blocker?.onModelChanged?.connect(() => {
      this.onBlockerChanged.emit(stack, blocker);
    });

    blocker?.onReleaseCollectable?.connect(() => {
      this.onReleaseCollectable.emit(stack, blocker);
    });

    this.blockers.push(blocker);

    if (blocker.underTile) {
      stack.setUnderTileBlocker(blocker);
    } else {
      stack.setBlocker(blocker);
    }

    return blocker;
  }

  public onPostLevelInitSetup(): void {
    this.blockers.slice().forEach((blocker) => {
      blocker?.postLevelSetupInit?.();
    });
  }

  public onMatchStart(stack: TileStackModel): void {
    this.blockers.slice().forEach((blocker) => {
      blocker?.onMatchStart?.(stack);
    });
  }

  public onMatch(stack: TileStackModel, tilesRemoved: TileModel[]): void {
    const matchId = generateId('match');

    this.blockers.slice().forEach((blocker) => {
      blocker.onMatch?.({
        matchId,
        stack,
        tilesRemoved,
        distantMatch: false,
      });
    });
  }

  public onScoreChanged(score: number): void {
    this.blockers.slice().forEach((blocker) => {
      blocker?.onScoreChanged?.(score);
    });
  }

  public onLevelBlockerTaskUpdate(taskType: LevelTaskType, progress: LevelTaskProgress): void {
    this.blockers.slice().forEach((blocker) => {
      blocker?.onLevelBlockerTaskUpdate?.(taskType, progress);
    });
  }

  public onLevelTaskComplete(taskType: LevelTaskType): void {
    this.blockers.slice().forEach((blocker) => {
      blocker?.onLevelTaskComplete?.(taskType);
    });
  }
}
